import { useState } from 'react'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import CustomInstructionsSettingsDialog from '@/settings/components/CustomInstructionsSettingsDialog'
import SectionHeader from '@/settings/components/SectionHeader'

type CustomInstructionsCardProps = {
  /** Saved value from the server. Empty string means the user has never set one. */
  savedInstructions: string
  /** False while the initial settings fetch is in flight. */
  settingsLoaded: boolean
  /** Persists the value. Rejects on failure, which this component surfaces inline. */
  onSave: (customInstructions: string) => Promise<void>
}

/**
 * Custom Instructions Card
 *
 * A user-scoped instruction block applied to every matter and every conversation. Explicit save
 * keeps typed text available when a request fails.
 */
export default function CustomInstructionsCard(props: CustomInstructionsCardProps) {
  const { savedInstructions, settingsLoaded, onSave } = props
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <>
      <div className="flex flex-col overflow-hidden rounded-lg border border-border-default bg-surface-emphasis">
        <SectionHeader title="Personalization" />
        <div className="py-4">
          <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-semibold leading-7 text-text-primary">Custom Instructions</p>
              <p className="text-sm leading-5 tracking-[-0.15px] text-text-tertiary">
                Describe how you work and control how Paxton should respond. You can override anytime during a chat.
              </p>

              {!settingsLoaded ? (
                <div className="mt-2 flex min-h-5 items-center" role="status" aria-label="Loading custom instructions">
                  <CircularProgressContinuousSized size={14} thickness={6} />
                </div>
              ) : savedInstructions ? (
                <p className="mt-2 line-clamp-1 min-w-0 break-all text-sm italic leading-5 tracking-[-0.15px] text-text-secondary" title={savedInstructions}>
                  {savedInstructions}
                </p>
              ) : null}
            </div>

            <Button
              variant="tonal"
              color={savedInstructions ? 'neutral' : 'primary'}
              size="sm"
              onClick={() => setDialogOpen(true)}
              disabled={!settingsLoaded}
              className="self-start sm:self-auto"
              aria-label={`${savedInstructions ? 'Edit' : 'Configure'} custom instructions`}
            >
              {savedInstructions ? 'Edit' : 'Configure'}
            </Button>
          </div>
        </div>
      </div>

      <CustomInstructionsSettingsDialog open={dialogOpen} onOpenChange={setDialogOpen} initialInstructions={savedInstructions} onSave={onSave} />
    </>
  )
}
